import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import {
    createEmptyPpuState,
    NES_SCREEN_HEIGHT,
    NES_SCREEN_WIDTH,
    renderFrame,
    type NesPpuState,
    type Mirroring,
} from "../../utils/nesPpu";
import { synthesizeChr, type ChrMotif } from "../../utils/nesChrSynth";
import {
    clampImageImportZoom,
    imageDataToPpuBackground,
    isValidImageColorConsistency,
    isValidImageFrameFit,
    isValidImagePaletteStrategy,
    loadImageToFrameImageData,
    type ImageColorConsistency,
    type ImageFrameFit,
    type ImageImportResult,
    type ImagePaletteStrategy,
} from "../../utils/nesChrFromImage";
import {
    applyGlitches,
    DEFAULT_GLITCH_PARAMS,
    randomGlitchParams,
    type GlitchParams,
} from "../../utils/nesGlitch";
import { isReservedBlack } from "../../utils/nesPalette";
import {
    buildSvgDocument,
    renderPixels,
    type OutputMode,
} from "../../utils/pixelSvgRender";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";

export type ChrSource = "synthetic" | "image";

const DEFAULT_SEED = 1234;
const DEFAULT_MOTIF: ChrMotif = "stripes";
const DEFAULT_OUTPUT_MODE: OutputMode = "greedy-rects";
const DEFAULT_OUTPUT_WIDTH = 512;
const DEFAULT_IMAGE_PALETTE_STRATEGY: ImagePaletteStrategy = "block-kmeans";
const DEFAULT_IMAGE_FRAME_FIT: ImageFrameFit = "cover-center";
const DEFAULT_IMAGE_IMPORT_ZOOM = 1;
const DEFAULT_IMAGE_COLOR_CONSISTENCY: ImageColorConsistency = "none";

export const CHR_MOTIFS: { id: ChrMotif; label: string }[] = [
    { id: "noise", label: "Noise" },
    { id: "stripes", label: "Stripes" },
    { id: "diamonds", label: "Diamonds" },
    { id: "bricks", label: "Bricks" },
    { id: "circuit", label: "Circuit" },
];

const OUTPUT_WIDTH_MIN = 64;
const OUTPUT_WIDTH_MAX = 2048;

interface NesGlitchValue {
    /** Final state after glitches; what the viewer renders. */
    glitchedState: NesPpuState;
    /** Indexed framebuffer (master-palette indices). */
    indexedFrame: Uint8Array;
    /** Opaque-RGBA framebuffer (matches `pixelSvgRender` packing). */
    rgbaFrame: Uint32Array;

    chrSource: ChrSource;
    setChrSource: (s: ChrSource) => void;
    seed: number;
    setSeed: (n: number) => void;
    motif: ChrMotif;
    setMotif: (m: ChrMotif) => void;

    /** True if the user imported an image (source = "image" and we have data). */
    hasImportedImage: boolean;
    /** Last imported image label, for display. */
    importedImageName: string | null;
    importImageFromBlob: (blob: Blob, name?: string) => Promise<void>;
    clearImportedImage: () => void;
    importStatus: string | null;

    /** How palette RAM is derived when importing an image (ignored for synthetic source). */
    imagePaletteStrategy: ImagePaletteStrategy;
    setImagePaletteStrategy: (s: ImagePaletteStrategy) => void;
    /** Map source bitmap to 256x240 before quantization (image import only). */
    imageFrameFit: ImageFrameFit;
    setImageFrameFit: (f: ImageFrameFit) => void;
    imageImportZoom: number;
    setImageImportZoom: (n: number) => void;
    imageColorConsistency: ImageColorConsistency;
    setImageColorConsistency: (m: ImageColorConsistency) => void;

    /** Bg pattern-table selection (before bankSwap glitch). */
    bgPatternTable: 0 | 1;
    setBgPatternTable: (n: 0 | 1) => void;
    sprPatternTable: 0 | 1;
    setSprPatternTable: (n: 0 | 1) => void;

    scrollX: number;
    setScrollX: (n: number) => void;
    scrollY: number;
    setScrollY: (n: number) => void;
    mirroring: Mirroring;
    setMirroring: (m: Mirroring) => void;

    /** Manual palette override (32 master indices). */
    paletteOverride: number[] | null;
    setPaletteEntry: (idx: number, masterIndex: number) => void;
    clearPaletteOverride: () => void;

    glitchParams: GlitchParams;
    setGlitchParams: (next: GlitchParams) => void;
    randomizeGlitchSeed: () => void;

    outputMode: OutputMode;
    setOutputMode: (m: OutputMode) => void;
    outputWidth: number;
    setOutputWidth: (n: number) => void;
    showOutputPane: boolean;
    setShowOutputPane: (v: boolean) => void;

    buildExportSvgString: () => string;
    downloadSvg: () => void;

    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
    /** Randomize procedural seed, motif, scroll, banks, mirroring, glitch stack, and clear palette override. Does not clear the imported image. */
    randomizeParameters: () => void;
}

const Ctx = createContext<NesGlitchValue | null>(null);

export function useNesGlitch(): NesGlitchValue {
    const v = useContext(Ctx);
    if (!v)
        throw new Error("useNesGlitch must be used within NesGlitchProvider");
    return v;
}

const DEFAULT_PRESET: Record<string, unknown> = {
    chrSource: "synthetic",
    seed: DEFAULT_SEED,
    motif: DEFAULT_MOTIF,
    bgPatternTable: 0,
    sprPatternTable: 1,
    scrollX: 0,
    scrollY: 0,
    mirroring: "horizontal",
    paletteOverride: null,
    glitchParams: DEFAULT_GLITCH_PARAMS,
    outputMode: DEFAULT_OUTPUT_MODE,
    outputWidth: DEFAULT_OUTPUT_WIDTH,
    showOutputPane: false,
    imagePaletteStrategy: DEFAULT_IMAGE_PALETTE_STRATEGY,
    imageFrameFit: DEFAULT_IMAGE_FRAME_FIT,
    imageImportZoom: DEFAULT_IMAGE_IMPORT_ZOOM,
    imageColorConsistency: DEFAULT_IMAGE_COLOR_CONSISTENCY,
};

interface ProviderProps {
    children: ReactNode;
}

export function NesGlitchProvider({ children }: ProviderProps) {
    const [chrSource, setChrSource] = useState<ChrSource>("synthetic");
    const [seed, setSeed] = useState<number>(DEFAULT_SEED);
    const [motif, setMotif] = useState<ChrMotif>(DEFAULT_MOTIF);

    const [importedState, setImportedState] = useState<NesPpuState | null>(
        null,
    );
    const [importedImageName, setImportedImageName] = useState<string | null>(
        null,
    );
    const [importStatus, setImportStatus] = useState<string | null>(null);
    const [imagePaletteStrategy, setImagePaletteStrategyState] =
        useState<ImagePaletteStrategy>(DEFAULT_IMAGE_PALETTE_STRATEGY);
    const [imageFrameFit, setImageFrameFitState] = useState<ImageFrameFit>(
        DEFAULT_IMAGE_FRAME_FIT,
    );
    const [imageImportZoom, setImageImportZoomState] = useState(
        DEFAULT_IMAGE_IMPORT_ZOOM,
    );
    const [imageColorConsistency, setImageColorConsistencyState] =
        useState<ImageColorConsistency>(DEFAULT_IMAGE_COLOR_CONSISTENCY);
    const lastImportBlobRef = useRef<Blob | null>(null);

    const setImageImportZoom = useCallback((n: number) => {
        setImageImportZoomState(clampImageImportZoom(n));
    }, []);

    const [bgPatternTable, setBgPatternTable] = useState<0 | 1>(0);
    const [sprPatternTable, setSprPatternTable] = useState<0 | 1>(1);
    const [scrollX, setScrollX] = useState(0);
    const [scrollY, setScrollY] = useState(0);
    const [mirroring, setMirroring] = useState<Mirroring>("horizontal");

    const [paletteOverride, setPaletteOverride] = useState<number[] | null>(
        null,
    );

    const [glitchParams, setGlitchParams] = useState<GlitchParams>(
        DEFAULT_GLITCH_PARAMS,
    );

    const [outputMode, setOutputMode] =
        useState<OutputMode>(DEFAULT_OUTPUT_MODE);
    const [outputWidth, setOutputWidthState] =
        useState<number>(DEFAULT_OUTPUT_WIDTH);
    const [showOutputPane, setShowOutputPane] = useState(true);

    const setOutputWidth = useCallback((n: number) => {
        if (!Number.isFinite(n)) return;
        const clamped = Math.max(
            OUTPUT_WIDTH_MIN,
            Math.min(OUTPUT_WIDTH_MAX, Math.round(n)),
        );
        setOutputWidthState(clamped);
    }, []);

    /** Compute the *base* PPU state (before glitches) from the chosen source. */
    const baseState = useMemo<NesPpuState>(() => {
        const empty = createEmptyPpuState();
        if (chrSource === "image" && importedState) {
            // Start from the imported state and overlay user choices.
            const s: NesPpuState = {
                ...importedState,
                patternTables: [
                    new Uint8Array(importedState.patternTables[0]),
                    new Uint8Array(importedState.patternTables[1]),
                ],
                nametable: new Uint8Array(importedState.nametable),
                attribute: new Uint8Array(importedState.attribute),
                palette: new Uint8Array(importedState.palette),
                oam: new Uint8Array(importedState.oam),
                bgPatternTable,
                sprPatternTable,
                scrollX,
                scrollY,
                mirroring,
            };
            if (paletteOverride) {
                for (let i = 0; i < 32 && i < paletteOverride.length; i++) {
                    s.palette[i] = paletteOverride[i] & 0x3f;
                }
            }
            return s;
        }
        // Synthetic.
        const synth = synthesizeChr(seed, motif);
        empty.patternTables = synth.patternTables;
        empty.nametable = synth.nametable;
        empty.attribute = synth.attribute;
        empty.bgPatternTable = bgPatternTable;
        empty.sprPatternTable = sprPatternTable;
        empty.scrollX = scrollX;
        empty.scrollY = scrollY;
        empty.mirroring = mirroring;
        if (paletteOverride) {
            for (let i = 0; i < 32 && i < paletteOverride.length; i++) {
                empty.palette[i] = paletteOverride[i] & 0x3f;
            }
        }
        return empty;
    }, [
        chrSource,
        importedState,
        seed,
        motif,
        bgPatternTable,
        sprPatternTable,
        scrollX,
        scrollY,
        mirroring,
        paletteOverride,
    ]);

    /** Apply the glitch pipeline whenever inputs change. */
    const glitchedState = useMemo(
        () => applyGlitches(baseState, glitchParams),
        [baseState, glitchParams],
    );

    /** Render the current frame. Memoized so canvas + SVG share work. */
    const frame = useMemo(() => renderFrame(glitchedState), [glitchedState]);

    const applyImportResult = useCallback((result: ImageImportResult) => {
        const empty = createEmptyPpuState();
        const next: NesPpuState = {
            ...empty,
            patternTables: [result.patternTable, result.altPatternTable],
            nametable: result.nametable,
            attribute: result.attribute,
            palette: result.palette,
        };
        setImportedState(next);
    }, []);

    const importImageFromBlob = useCallback(
        async (blob: Blob, name?: string) => {
            lastImportBlobRef.current = blob;
            setImportStatus("Importing...");
            try {
                const imageData = await loadImageToFrameImageData(blob, {
                    fit: imageFrameFit,
                    zoom: imageImportZoom,
                });
                const result = imageDataToPpuBackground(imageData, {
                    strategy: imagePaletteStrategy,
                    colorConsistency: imageColorConsistency,
                });
                applyImportResult(result);
                setImportedImageName(name ?? "image");
                setChrSource("image");
                setPaletteOverride(null);
                const tail = result.overflow
                    ? ` (${result.uniqueTiles} tiles used; some duplicates collapsed)`
                    : ` (${result.uniqueTiles} unique tiles)`;
                setImportStatus(`Imported${tail}`);
            } catch (err) {
                lastImportBlobRef.current = null;
                setImportStatus(
                    err instanceof Error ? err.message : "Import failed",
                );
            }
        },
        [
            applyImportResult,
            imagePaletteStrategy,
            imageFrameFit,
            imageImportZoom,
            imageColorConsistency,
        ],
    );

    useEffect(() => {
        const blob = lastImportBlobRef.current;
        if (!blob) return;
        let cancelled = false;
        (async () => {
            try {
                setImportStatus("Rebuilding...");
                const imageData = await loadImageToFrameImageData(blob, {
                    fit: imageFrameFit,
                    zoom: imageImportZoom,
                });
                if (cancelled) return;
                const result = imageDataToPpuBackground(imageData, {
                    strategy: imagePaletteStrategy,
                    colorConsistency: imageColorConsistency,
                });
                if (cancelled) return;
                applyImportResult(result);
                setPaletteOverride(null);
                const tail = result.overflow
                    ? ` (${result.uniqueTiles} tiles; some duplicates collapsed)`
                    : ` (${result.uniqueTiles} tiles)`;
                setImportStatus(`Import options updated${tail}`);
            } catch (err) {
                if (!cancelled) {
                    setImportStatus(
                        err instanceof Error ? err.message : "Rebuild failed",
                    );
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [
        imagePaletteStrategy,
        imageFrameFit,
        imageImportZoom,
        imageColorConsistency,
        applyImportResult,
    ]);

    const clearImportedImage = useCallback(() => {
        lastImportBlobRef.current = null;
        setImportedState(null);
        setImportedImageName(null);
        setImportStatus(null);
        setChrSource("synthetic");
    }, []);

    const setPaletteEntry = useCallback(
        (idx: number, masterIndex: number) => {
            setPaletteOverride((prev) => {
                const base = prev ? [...prev] : Array.from(baseState.palette);
                let v = (((masterIndex | 0) % 64) + 64) % 64;
                let safety = 0;
                while (isReservedBlack(v) && safety < 8) {
                    v = (v + 1) & 0x3f;
                    safety++;
                }
                base[idx] = v;
                return base;
            });
        },
        [baseState.palette],
    );

    const clearPaletteOverride = useCallback(() => {
        setPaletteOverride(null);
    }, []);

    const randomizeGlitchSeed = useCallback(() => {
        setGlitchParams((p) => ({
            ...p,
            seed: Math.floor(Math.random() * 0xffffffff),
        }));
    }, []);

    const randomizeParameters = useCallback(() => {
        setSeed((Math.floor(Math.random() * 0x1_0000_0000) | 0) >>> 0);
        setMotif(CHR_MOTIFS[Math.floor(Math.random() * CHR_MOTIFS.length)].id);
        setScrollX(Math.floor(Math.random() * 256));
        setScrollY(Math.floor(Math.random() * 240));
        setMirroring(Math.random() < 0.5 ? "horizontal" : "vertical");
        setBgPatternTable(Math.random() < 0.5 ? 0 : 1);
        setSprPatternTable(Math.random() < 0.5 ? 0 : 1);
        setPaletteOverride(null);
        setGlitchParams(randomGlitchParams());
    }, []);

    const buildExportSvgString = useCallback((): string => {
        const result = renderPixels(
            frame.rgba,
            NES_SCREEN_WIDTH,
            NES_SCREEN_HEIGHT,
            outputMode,
            outputWidth,
            Math.round((outputWidth * NES_SCREEN_HEIGHT) / NES_SCREEN_WIDTH),
        );
        return buildSvgDocument(
            result.body,
            outputWidth,
            Math.round((outputWidth * NES_SCREEN_HEIGHT) / NES_SCREEN_WIDTH),
        );
    }, [frame.rgba, outputMode, outputWidth]);

    const downloadSvg = useCallback(() => {
        const doc = buildExportSvgString();
        const blob = new Blob([doc], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `nes-glitch-${outputWidth}px-${Date.now()}.svg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [buildExportSvgString, outputWidth]);

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            chrSource,
            seed,
            motif,
            bgPatternTable,
            sprPatternTable,
            scrollX,
            scrollY,
            mirroring,
            paletteOverride: paletteOverride ?? null,
            glitchParams,
            outputMode,
            outputWidth,
            showOutputPane,
            imagePaletteStrategy,
            imageFrameFit,
            imageImportZoom,
            imageColorConsistency,
        }),
        [
            chrSource,
            seed,
            motif,
            bgPatternTable,
            sprPatternTable,
            scrollX,
            scrollY,
            mirroring,
            paletteOverride,
            glitchParams,
            outputMode,
            outputWidth,
            showOutputPane,
            imagePaletteStrategy,
            imageFrameFit,
            imageImportZoom,
            imageColorConsistency,
        ],
    );

    const applyPreset = useCallback(
        (values: Record<string, unknown>) => {
            if (typeof values.chrSource === "string") {
                const v = values.chrSource;
                if (v === "synthetic" || v === "image") setChrSource(v);
            }
            if (typeof values.seed === "number") setSeed(values.seed | 0);
            if (typeof values.motif === "string") {
                const m = values.motif as ChrMotif;
                if (CHR_MOTIFS.some((x) => x.id === m)) setMotif(m);
            }
            if (values.bgPatternTable === 0 || values.bgPatternTable === 1) {
                setBgPatternTable(values.bgPatternTable);
            }
            if (values.sprPatternTable === 0 || values.sprPatternTable === 1) {
                setSprPatternTable(values.sprPatternTable);
            }
            if (typeof values.scrollX === "number")
                setScrollX(clampInt(values.scrollX, 0, 255));
            if (typeof values.scrollY === "number")
                setScrollY(clampInt(values.scrollY, 0, 239));
            if (
                values.mirroring === "horizontal" ||
                values.mirroring === "vertical"
            ) {
                setMirroring(values.mirroring);
            }
            if (
                typeof values.imagePaletteStrategy === "string" &&
                isValidImagePaletteStrategy(values.imagePaletteStrategy)
            ) {
                setImagePaletteStrategyState(values.imagePaletteStrategy);
            }
            if (
                typeof values.imageFrameFit === "string" &&
                isValidImageFrameFit(values.imageFrameFit)
            ) {
                setImageFrameFitState(values.imageFrameFit);
            }
            if (typeof values.imageImportZoom === "number") {
                setImageImportZoomState(
                    clampImageImportZoom(values.imageImportZoom),
                );
            }
            if (
                typeof values.imageColorConsistency === "string" &&
                isValidImageColorConsistency(values.imageColorConsistency)
            ) {
                setImageColorConsistencyState(values.imageColorConsistency);
            }
            if (Array.isArray(values.paletteOverride)) {
                const arr = values.paletteOverride.filter(
                    (v): v is number => typeof v === "number",
                );
                setPaletteOverride(arr.length > 0 ? arr.slice(0, 32) : null);
            } else if (values.paletteOverride === null) {
                setPaletteOverride(null);
            }
            if (
                values.glitchParams &&
                typeof values.glitchParams === "object" &&
                !Array.isArray(values.glitchParams)
            ) {
                const incoming = values.glitchParams as Partial<GlitchParams>;
                setGlitchParams({
                    ...DEFAULT_GLITCH_PARAMS,
                    ...incoming,
                    nametableShift: {
                        ...DEFAULT_GLITCH_PARAMS.nametableShift,
                        ...(incoming.nametableShift ?? {}),
                    },
                    nametableNoise: {
                        ...DEFAULT_GLITCH_PARAMS.nametableNoise,
                        ...(incoming.nametableNoise ?? {}),
                    },
                    nametableScroll: {
                        ...DEFAULT_GLITCH_PARAMS.nametableScroll,
                        ...(incoming.nametableScroll ?? {}),
                    },
                    attributeRandomize: {
                        ...DEFAULT_GLITCH_PARAMS.attributeRandomize,
                        ...(incoming.attributeRandomize ?? {}),
                    },
                    paletteHueShift: {
                        ...DEFAULT_GLITCH_PARAMS.paletteHueShift,
                        ...(incoming.paletteHueShift ?? {}),
                    },
                    paletteSwap: {
                        ...DEFAULT_GLITCH_PARAMS.paletteSwap,
                        ...(incoming.paletteSwap ?? {}),
                    },
                    chrBitFlip: {
                        ...DEFAULT_GLITCH_PARAMS.chrBitFlip,
                        ...(incoming.chrBitFlip ?? {}),
                    },
                    oamNoise: {
                        ...DEFAULT_GLITCH_PARAMS.oamNoise,
                        ...(incoming.oamNoise ?? {}),
                    },
                    bankSwap: {
                        ...DEFAULT_GLITCH_PARAMS.bankSwap,
                        ...(incoming.bankSwap ?? {}),
                    },
                    mirroringSwap: {
                        ...DEFAULT_GLITCH_PARAMS.mirroringSwap,
                        ...(incoming.mirroringSwap ?? {}),
                    },
                });
            }
            if (
                values.outputMode === "raw" ||
                values.outputMode === "greedy-rects" ||
                values.outputMode === "paths"
            ) {
                setOutputMode(values.outputMode);
            }
            if (typeof values.outputWidth === "number") {
                setOutputWidth(values.outputWidth);
            }
            if (typeof values.showOutputPane === "boolean") {
                setShowOutputPane(values.showOutputPane);
            }
        },
        [setOutputWidth],
    );

    const { resetParameters: resetParamsRaw } = useToolParamPersistence({
        toolId: "nes-glitch",
        defaultPreset: DEFAULT_PRESET,
        getPresetValues,
        applyPreset,
    });

    const resetParameters = useCallback(() => {
        clearImportedImage();
        resetParamsRaw();
    }, [clearImportedImage, resetParamsRaw]);

    // Re-derive indexed/rgba so consumers don't pull from `frame` directly when
    // it changes object identity (cheap; arrays are already there).
    const indexedFrame = frame.indexed;
    const rgbaFrame = frame.rgba;

    // Make sure that when source switches between synthetic and image but we
    // don't have an imported image, we don't strand the user.
    useEffect(() => {
        if (chrSource === "image" && !importedState) {
            setChrSource("synthetic");
        }
    }, [chrSource, importedState]);

    const value: NesGlitchValue = useMemo(
        () => ({
            glitchedState,
            indexedFrame,
            rgbaFrame,
            chrSource,
            setChrSource,
            seed,
            setSeed,
            motif,
            setMotif,
            hasImportedImage: !!importedState,
            importedImageName,
            importImageFromBlob,
            clearImportedImage,
            importStatus,
            imagePaletteStrategy,
            setImagePaletteStrategy: setImagePaletteStrategyState,
            imageFrameFit,
            setImageFrameFit: setImageFrameFitState,
            imageImportZoom,
            setImageImportZoom,
            imageColorConsistency,
            setImageColorConsistency: setImageColorConsistencyState,
            bgPatternTable,
            setBgPatternTable,
            sprPatternTable,
            setSprPatternTable,
            scrollX,
            setScrollX,
            scrollY,
            setScrollY,
            mirroring,
            setMirroring,
            paletteOverride,
            setPaletteEntry,
            clearPaletteOverride,
            glitchParams,
            setGlitchParams,
            randomizeGlitchSeed,
            outputMode,
            setOutputMode,
            outputWidth,
            setOutputWidth,
            showOutputPane,
            setShowOutputPane,
            buildExportSvgString,
            downloadSvg,
            applyPreset,
            getPresetValues,
            resetParameters,
            randomizeParameters,
        }),
        [
            glitchedState,
            indexedFrame,
            rgbaFrame,
            chrSource,
            seed,
            motif,
            importedState,
            importedImageName,
            importImageFromBlob,
            clearImportedImage,
            importStatus,
            imagePaletteStrategy,
            imageFrameFit,
            imageImportZoom,
            setImageImportZoom,
            imageColorConsistency,
            bgPatternTable,
            sprPatternTable,
            scrollX,
            scrollY,
            mirroring,
            paletteOverride,
            setPaletteEntry,
            clearPaletteOverride,
            glitchParams,
            randomizeGlitchSeed,
            randomizeParameters,
            outputMode,
            outputWidth,
            setOutputWidth,
            showOutputPane,
            buildExportSvgString,
            downloadSvg,
            applyPreset,
            getPresetValues,
            resetParameters,
        ],
    );

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function clampInt(v: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, Math.round(v)));
}
